import type { ApiClient } from '@acies/platform';
import type { Effect } from 'effect';
import type { SearchResult } from '../domain/search-results';
import { runCall } from './runtime';
import { listCached, type SearchError, searchAt } from './search';

/** Everything listing an inventory item's configurations can fail with, which is what a search can. */
export type ListInventoryItemConfigurationsError = SearchError;

/** The calls against the store's inventory item configurations. */
export interface InventoryItemConfigurationCalls {
	/**
	 * Lists the configurations one inventory item can be had in, at the link the engine names the list by.
	 *
	 * The link rather than the item, because the engine hands it over: a stock configuration of an item that comes in configurations carries it — see `StockConfiguration.itemConfigurations` — already filtered by the item, so the list is asked for where the engine says it is rather than at a path built beside it.
	 *
	 * Listed rather than searched, because an item comes in few enough to offer in a dropdown. Each answers as a name and a path, the way a search result does, so the key — `K001` — is read off the path like any other.
	 */
	readonly listInventoryItemConfigurations: (url: string) => Effect.Effect<ReadonlyArray<SearchResult>, ListInventoryItemConfigurationsError>;
}

/**
 * Implements the inventory item configuration calls over the client.
 *
 * `typeof ApiClient.Service` is the shape `ApiClient` provides — the client with this API's host, bearer token, account header, auth failures and retry policy already applied.
 */
export function makeInventoryItemConfigurationCalls(client: typeof ApiClient.Service): InventoryItemConfigurationCalls {
	return {
		listInventoryItemConfigurations: (url) => searchAt(client, url),
	};
}

/**
 * Lists one inventory item's configurations from outside Effect, answering from the search cache where it can.
 *
 * The awaitable form of {@link InventoryItemConfigurationCalls.listInventoryItemConfigurations}, for the dropdown a stock position's configuration is picked in, which is a web component rather than an Effect program. A failure comes back as itself — see {@link runCall} — so the dropdown can go on offering nothing rather
 * than offering something that is not there.
 *
 * Kept for as long as the page lives, the way a search is, one list per link: the configurations an item comes in do not change while someone is working on a position, and a user who comes back to a position is answered from here.
 */
export const listInventoryItemConfigurations = (url: string): Promise<ReadonlyArray<SearchResult> | ListInventoryItemConfigurationsError> => runCall(() => listCached(url));
